import type { ContentHint } from '@live-stream/media'
import type { ScreenSurface } from '@live-stream/shared'

/**
 * The pure part of screen sharing: what the browser told us about a
 * capture, what to favour when encoding it, and how to say it to a person.
 * The composable owns getDisplayMedia; the store owns publishing; this
 * file only knows the rules, and runs under vitest with no DOM.
 */

/** The browser's word for what was picked, in our vocabulary. Missing or unknown values are 'unknown'. */
export function surfaceOf(settings: { displaySurface?: unknown } | undefined): ScreenSurface {
  const raw = settings?.displaySurface
  if (raw === 'monitor' || raw === 'window' || raw === 'browser') return raw
  return 'unknown'
}

/**
 * What the encoder should favour, before the person says otherwise. A tab
 * shared with its sound is almost always a video playing — keep it moving.
 * Everything else — a window, a whole screen, a silent tab — is documents,
 * code and slides, where text must stay legible: keep it sharp and let the
 * frame rate go. Meet's default is the same, with the same switch to flip it.
 */
export function defaultHint(capture: { surface: ScreenSurface; hasAudio: boolean }): ContentHint {
  return capture.surface === 'browser' && capture.hasAudio ? 'motion' : 'detail'
}

/** "your entire screen", "a window", "a tab" — for the presenter's own pill. */
export function describeSurface(surface: ScreenSurface): string {
  switch (surface) {
    case 'monitor':
      return 'your entire screen'
    case 'window':
      return 'a window'
    case 'browser':
      return 'a tab'
    default:
      return 'your screen'
  }
}

/** The same, from the other side: "their screen", "a window", "a tab". */
export function describeSurfaceFor(surface: ScreenSurface, name: string): string {
  switch (surface) {
    case 'monitor':
      return `${name}'s screen`
    case 'window':
      return `a window from ${name}`
    case 'browser':
      return `a tab from ${name}`
    default:
      return `${name}'s screen`
  }
}

export type CaptureFailure = 'cancelled' | 'unsupported' | 'denied' | 'failed'

/**
 * Why getDisplayMedia did not hand us a stream. The person closing the
 * picker is the common case and is not an error — nothing should be said.
 * A policy or a permission refusing is worth one sentence; a browser with
 * no picker at all (phones, mostly) is worth saying before the button.
 */
export function classifyCaptureError(err: unknown): CaptureFailure {
  const name = (err as { name?: string } | null)?.name ?? ''
  if (name === 'AbortError') return 'cancelled'
  if (name === 'NotAllowedError') {
    // Chrome uses NotAllowedError both for "the user dismissed the picker"
    // and for "policy forbids it"; the message tells them apart.
    const message = String((err as { message?: string } | null)?.message ?? '').toLowerCase()
    return message.includes('permission') && message.includes('denied') ? 'denied' : 'cancelled'
  }
  if (name === 'NotSupportedError' || name === 'TypeError') return 'unsupported'
  return 'failed'
}

export function captureFailureMessage(failure: CaptureFailure): string | null {
  switch (failure) {
    case 'cancelled':
      return null
    case 'unsupported':
      return 'This browser cannot share a screen. On a computer, use Chrome, Edge, Firefox or Safari.'
    case 'denied':
      return 'Screen sharing is blocked for this site. Allow it in the browser’s site settings and try again.'
    default:
      return 'The screen could not be captured. Try again, or pick a different window.'
  }
}
